import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import cN from 'classnames';
import { Calendar, ICalendarProps, IDetailDateData } from '@lion-design/core';
import { GroupAllGuaranteed } from '@components/DetailPage';
import {
    setGroupDetail,
    setGroupAllCalendar,
    setCalendarGroupsMonth,
    setStationInfo,
    setPricesInfo,
    setFlightInfo,
    setPricesInfoSUCCESS,
    setChooseDateM,
} from '@Redux/detail/Group/actions';
import { setItineraries, setOptionalInfo, setNoticeinfo } from '@Redux/detail/Itineraries/actions';
import * as Interface from '@Redux/detail/Group/interfaces';
import { RootState } from '@Redux/detail/store';
import { ArrowLeft } from '@components/Common/Icons';
import Spinner from '@components/Common/Spinner';
import { changeDetailHistory } from '@utils/utils';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';

interface I_Price {
    price: string;
    isMobile: boolean;
}

interface I_GroupLabel {
    Guaranteed: boolean;
}

interface I_GroupState {
    Status: string;
    multiTrip: boolean;
}

interface I_DetailInfo {
    item: Interface.I_CalendarGroup;
    multiTrip: boolean;
    slidePageVisible: Function;
    isMobile: boolean;
}

interface I_Props {
    slidePageVisible?: any;
}

function GroupAllCalendar({ slidePageVisible }: I_Props) {
    const { preferairlines } = document.getElementById('detailPagePara')!.dataset!;
    const { GroupInfo, Schedules, currentTourID, isLoadingTours, FestivalDates } = useSelector(
        (store: RootState) => store.groupReducer
    );
    const { CalendarGroups } = useSelector((state: RootState) => state.calendarInfoReducer);

    const dispatch = useDispatch();
    const { isLoadingCalendar } = CalendarGroups;
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });

    const convertToObjectArray = (obj: object) => {
        return Object.entries(obj).map(([date, festival]) => ({
            date,
            festival,
        }));
    };

    const festivalArray = convertToObjectArray(FestivalDates);

    useEffect(() => {
        dispatch(setChooseDateM(dayjs(GroupInfo.GoDate).format('YYYY-MM-DD')));
    }, [GroupInfo.GoDate, dispatch]);

    /*
        detailDateData 範例：
        {
            '2021-03-26': { note: <月曆內文 /> },
            '2021-03-27': { note: <月曆內文 /> },
            '2021-03-28': { note: <月曆內文 /> },
            ...
        }
    */
    const detailDateData: () => IDetailDateData | undefined = () => {
        if (CalendarGroups?.groups?.length > 0) {
            const detailDateData: IDetailDateData = {};
            const guaranteedData = CalendarGroups.groups?.filter((group) => {
                return group.Guaranteed == true;
            });
            const renderData =
                !CalendarGroups.isGuaranteedDisabled && CalendarGroups.parmIsEnsureGroup
                    ? guaranteedData
                    : CalendarGroups?.groups;

            renderData.forEach((item: Interface.I_CalendarGroup) => {
                // 將 2021/03/26 轉換為 2021-03-26
                const reFormatDate = item.Date.replace(/\//g, '-');

                const multiTrip = Object.prototype.hasOwnProperty.call(
                    detailDateData,
                    reFormatDate
                );

                detailDateData[reFormatDate] = {
                    note: (
                        <DetailInfo
                            item={
                                multiTrip
                                    ? {
                                          ...detailDateData[reFormatDate].note.props.item,
                                      }
                                    : item
                            }
                            multiTrip={multiTrip}
                            slidePageVisible={slidePageVisible}
                            isMobile={isMobile}
                        />
                    ),
                };
            });

            return detailDateData;
        }
    };

    // 以行程 API (tourinfojson) 中得知最先&最後出發日期，得知最小&最大月份
    const isMaxMonth = (function compareScheduleMax() {
        const MaxMonth = dayjs(Schedules?.AllMaxGoDate).format('YYYY-MM');
        return !Schedules?.AllMaxGoDate
            ? true
            : dayjs(CalendarGroups.month).isSameOrAfter(MaxMonth);
    })();

    const isMinMonth = (function compareScheduleMin() {
        const MinMonth = dayjs(Schedules?.AllMinGoDate).format('YYYY-MM');
        return !Schedules?.AllMinGoDate
            ? true
            : dayjs(CalendarGroups.month).isSameOrBefore(MinMonth);
    })();

    const MaxMonthDate = dayjs(Schedules && Schedules.AllMaxGoDate).format('YYYY-MM-DD');
    const MinMonthDate = dayjs(Schedules && Schedules.AllMinGoDate).format('YYYY-MM-DD');

    const getMonthStartAndEnd = (day: string) => {
        const start = dayjs(day).startOf('month').format('YYYY-MM-DD');
        const end = dayjs(day).endOf('month').format('YYYY-MM-DD');

        return [start, end];
    };

    const clickPrevOrNext = (condition: 'prev' | 'next') => {
        const month =
            condition === 'prev'
                ? dayjs(CalendarGroups.month).subtract(1, 'month').format('YYYY-MM')
                : dayjs(CalendarGroups.month).add(1, 'month').format('YYYY-MM');
        const [GoDateStart, GoDateEnd] = getMonthStartAndEnd(month);

        const reqBody = {
            NormGroupID: GroupInfo.NormGroupID,
            GoDateStart,
            GoDateEnd,
            TourID: currentTourID,
            preferairlines,
            TourSource: GroupInfo.TourSource,
        };

        dispatch(setCalendarGroupsMonth(month));
        dispatch(setGroupAllCalendar(reqBody));
        dispatch(setChooseDateM(''));
    };

    return (
        <div
            className={cN(styles['group_all_calendar'], 'group_all_Calendar', {
                [`${styles.show}`]: !CalendarGroups.isListMode || isMobile,
                [`${styles.mobile}`]: isMobile,
                [`${styles.noData}`]:
                    CalendarGroups.groups?.length < 1 && !(isLoadingTours || isLoadingCalendar),
            })}
        >
            {!isMobile && <GroupAllGuaranteed />}
            <Calendar
                mode="groupDetail"
                pickDate={GroupInfo.GoDate}
                detailDateData={detailDateData()}
                arrowPrevEle={<ArrowLeft fill={isMinMonth ? 'transparent' : '#f03742'} />}
                arrowNextEle={<ArrowLeft fill={isMaxMonth ? 'transparent' : '#f03742'} />}
                onClickPrevMonth={
                    isMinMonth
                        ? undefined
                        : () => {
                              clickPrevOrNext('prev');
                          }
                }
                onClickNextMonth={
                    isMaxMonth
                        ? undefined
                        : () => {
                              clickPrevOrNext('next');
                          }
                }
                isChineseMonth={false}
                curMonth={dayjs(CalendarGroups.month)}
                minDate={MinMonthDate}
                maxDate={MaxMonthDate}
                onChange={(val) => {
                    if (isMobile) {
                        dispatch(setChooseDateM(val));
                    }
                }}
                groupDetail
                festivalDate={FestivalDates}
            />
            {/* 國定假日顯示 */}
            {isMobile && (
                <ul className={styles.festivalDate}>
                    {festivalArray.map((festivals, index) => {
                        const [year, month, day] = festivals.date.split('-');
                        if (CalendarGroups.month === `${year}-${month}`) {
                            return (
                                <li key={index}>
                                    {festivals.festival}：{month}/{day}
                                </li>
                            );
                        }
                    })}
                </ul>
            )}
            {(isLoadingTours || isLoadingCalendar) && <Spinner />}
        </div>
    );
}

export default GroupAllCalendar;
export type I_GroupAllCalendar = ICalendarProps;

/**********************/
function digitCount(number: number) {
    number = Math.abs(number);
    return number.toString().length;
}

function Prices({ price, isMobile }: I_Price) {
    const priceStr = price.replace('起', '');
    const isPrice = price !== '近期上架';
    const isMillion = digitCount(Number(priceStr.replaceAll(',', ''))) >= 7;
    let plus;
    if (isPrice && price.length > 0) {
        plus = isMobile && isMillion ? '' : '+';
    }

    return (
        <div className={styles['price']}>
            {priceStr}
            {plus}
        </div>
    );
}

function GroupLable({ Guaranteed }: I_GroupLabel) {
    const { t } = useTranslation();

    return Guaranteed ? <div className={styles['groupLable']}>{t('IsEnsureGroup')}</div> : null;
}

function GroupState({ Status, multiTrip }: I_GroupState) {
    return multiTrip ? (
        <div className={styles['multiTrip']}>多方案</div>
    ) : (
        <div className={styles['status']}>{Status}</div>
    );
}

function clickDetailDate(
    dispatch: any,
    item: Interface.I_CalendarGroup,
    isMobile: boolean,
    TravelType: number,
    TourSource: string,
    GroupInfo_TourID: string
) {
    const { ID, Date, NormGroupID, TourID } = item;
    // m版點擊日期動作為切換至當日行程列表
    if (isMobile) {
        dispatch(setChooseDateM(Date));
    } else {
        const reqBody = {
            GroupID: ID,
            TourSource,
        };

        const FlightInfoReqBody = {
            GroupID: ID,
            NormGroupID,
            GoDate: dayjs(Date).format('YYYY-MM-DD'),
            TourSource,
        };

        // 切換不同行程安排時，需重新取得每日行程資料
        if (item.TourID !== GroupInfo_TourID) {
            const itinerariesReqBody = {
                TourID: TourID,
                IsPreview: false,
                TravelType: TravelType,
                TourSource: TourSource,
            };
            dispatch(setItineraries(itinerariesReqBody));
        }

        dispatch(setGroupDetail(reqBody));
        dispatch(setStationInfo(reqBody));
        dispatch(setOptionalInfo(reqBody));
        dispatch(setNoticeinfo(reqBody));
        dispatch(setPricesInfoSUCCESS(null)); // 為了避免點擊"費用說明"時,資料尚未更新即開啟光箱造成 bug
        dispatch(setPricesInfo(reqBody));
        dispatch(setFlightInfo(FlightInfoReqBody));
        changeDetailHistory({ NormGroupID, GroupID: ID });
    }
}

function DetailInfo({ item, multiTrip, isMobile }: I_DetailInfo) {
    const { Price, ID, Date } = item;
    const { GoDate, GroupID, TourSource, TourID, TravelType } = useSelector(
        (state: RootState) => state.groupReducer.GroupInfo
    );

    const dispatch = useDispatch();

    const isSameDay = dayjs(Date).isSame(GoDate);

    return (
        <div
            className={cN(styles['detailInfo'], { [styles.active]: isSameDay })}
            onClick={() => {
                ID !== GroupID &&
                    clickDetailDate(dispatch, item, isMobile, TravelType, TourSource, TourID);
            }}
        >
            <GroupLable Guaranteed={item.Guaranteed} />
            <div className={styles['contentBlock']}>
                <Prices price={Price} isMobile={isMobile} />
                <GroupState Status={item.Status} multiTrip={multiTrip} />
                {item.IsVip && <div className={styles['vip_wrapper']}>會員限購</div>}
            </div>
        </div>
    );
}
